// src/components/PostCard.jsx
import { useEffect, useState } from 'react';
import ReadMoreTextBase from '@components/readmore/ReadMoreTextBase';
import GetViews from '@components/GetViews';
import Comments from '@components/Comments';
import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';

import { db } from '@root/firebase';
import { doc, getDoc } from 'firebase/firestore';

function formatDateShort(ts) {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
  if (!d) return '';
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function PostCard({ post, currentUser, defaultAvatarUrl }) {
  const authorUid = post?.userId || post?.uid || null;

  const [authorName, setAuthorName] = useState(post?.userName || '');
  const [authorAvatar, setAuthorAvatar] = useState(
    post?.userAvatar || post?.avatar || ''
  );

  useEffect(() => {
    let mounted = true;
    async function run() {
      try {
        if (!authorUid) return;
        const snap = await getDoc(doc(db, 'users', authorUid));
        if (!mounted || !snap.exists()) return;
        const data = snap.data() || {};
        if (data.name || data.displayName) {
          setAuthorName(data.name || data.displayName);
        }
        if (data.avatar || data.photoURL) {
          setAuthorAvatar(data.avatar || data.photoURL);
        }
      } catch (e) {
        console.warn('PostCard: failed to load author profile', e);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [authorUid]);

  const createdLabel = formatDateShort(post?.createdAt);
  const collectionPath = 'posts';

  return (
    <div className='card'>
      <div className='card-body'>
        {/* Images (optional) */}
        {Array.isArray(post?.images) && post.images.length > 0 && (
          <div className='mb-2'>
            {post.images.map((img, idx) => (
              <img
                key={idx}
                className='img-fluid w-100 mb-2'
                src={img.url}
                alt={`post-${post.id}-${idx}`}
                style={{ objectFit: 'cover', borderRadius: 6 }}
              />
            ))}
          </div>
        )}

        {/* HEADER ROW — left: author, right: actions */}
        <div className='d-flex align-items-center justify-content-between'>
          {/* Left group: avatar + name + date */}
          <div className='d-flex align-items-center'>
            <img
              src={authorAvatar || defaultAvatarUrl}
              className='rounded-circle'
              alt={authorName || 'User'}
              style={{ width: 40, height: 40, objectFit: 'cover' }}
            />
            <div className='ms-2'>
              <div className='fw-semibold'>{authorName || 'User'}</div>
              <small className='text-muted'>{createdLabel}</small>
            </div>
          </div>

          {/* Right group: views / like / favorite */}
          <div className='d-flex align-items-center gap-2'>
            <GetViews docPath={`posts/${post.id}`} buttonSize='xs' />
            <LikeButton
              collectionPath={collectionPath}
              documentId={post.id}
              userId={post.uid}
              styled
            />
            <FavoriteButton
              collectionPath={collectionPath}
              documentId={post.id}
              userId={post.uid}
              styled
            />
          </div>
        </div>

        {/* Text */}
        {post?.text && (
          <div className='mt-2'>
            <ReadMoreTextBase text={post.text} wordLimit={5} readMore />
          </div>
        )}

        {/* COMMENTS — stays at the bottom, full width */}
        <div className='mt-3'>
          <Comments
            parentId={post.id}
            collectionPath={collectionPath}
            showCommentLabel={true}
            className='col-12'
            globalUser={currentUser}
          />
        </div>
      </div>
    </div>
  );
}
